import type { BrandConfig, ConverterTheme, HeroFeature } from "@batchworks/ui";
import {
  FiCpu,
  FiHeadphones,
  FiMusic,
  FiShield,
  FiVolume2,
} from "react-icons/fi";
import type { AudioChannels, AudioSampleRate, TargetMimeType } from "./types";

export const AUDIO_THEME: ConverterTheme = {
  focusRing: "focus-visible:ring-rose-500",
  logoBox: "bg-gradient-to-br from-rose-600 to-sky-500 shadow-rose-500/30",
  logoText: "from-rose-700 to-sky-600 dark:from-rose-300 dark:to-sky-300",
  accentText: "text-rose-600 dark:text-rose-300",
  accentSoftPill:
    "text-rose-700 dark:text-sky-200 bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800",
  featureIcon: "text-sky-600 dark:text-sky-300",
  uploadIconBox:
    "bg-gradient-to-br from-rose-100 to-sky-100 dark:from-rose-900/50 dark:to-sky-900/40",
  uploadIcon: "text-rose-700 dark:text-sky-300",
  dropActive:
    "border-sky-500 bg-sky-50/60 dark:bg-sky-900/10 scale-[1.01] shadow-xl shadow-sky-500/10",
  dropHover: "hover:border-sky-500 dark:hover:border-sky-500",
  primaryButton:
    "from-rose-600 to-sky-600 hover:from-rose-700 hover:to-sky-700",
  primaryShadow: "shadow-rose-500/20",
  progressBar: "from-rose-500 to-sky-500",
  settingsActiveButton:
    "bg-rose-50 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700 text-rose-600 dark:text-rose-400",
  settingsDot: "bg-rose-500",
  settingsIconBox: "bg-rose-500",
  sliderText: "text-rose-600 dark:text-rose-400",
  sliderTrack: "from-rose-500 to-sky-500",
  sliderThumb: "[&::-webkit-slider-thumb]:border-rose-500",
  queueIcon: "bg-rose-100 text-rose-500 dark:bg-rose-900/30",
  queueHoverBorder: "hover:border-rose-200 dark:hover:border-rose-800",
  resultIcon:
    "bg-rose-100 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-300",
  resultBadge: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
  resultDownloadButton:
    "bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/50 border-rose-200 dark:border-rose-800",
  linkHover: "hover:text-rose-600 dark:hover:text-rose-300",
};

export const AUDIO_BRAND: BrandConfig = {
  name: "AudioBatch",
  tagline: "Free Batch Audio Converter",
  appLabel: "audio converter",
  href: "/",
  repositoryUrl: "https://github.com/trevonerd/batchworks",
  profileUrl: "https://github.com/trevonerd",
  icon: FiVolume2,
};

export const HERO_FEATURES: HeroFeature[] = [
  { icon: FiShield, label: "Private, no-upload conversion" },
  { icon: FiCpu, label: "Local FFmpeg processing" },
  { icon: FiHeadphones, label: "Batch audio converter" },
];

export const FORMAT_OPTIONS: Array<{ value: TargetMimeType; label: string }> = [
  { value: "audio/mpeg", label: "MP3" },
  { value: "audio/wav", label: "WAV" },
  { value: "audio/mp4", label: "M4A" },
  { value: "audio/flac", label: "FLAC" },
  { value: "audio/ogg", label: "OGG" },
];

export const SAMPLE_RATE_OPTIONS: Array<{
  value: AudioSampleRate;
  label: string;
}> = [
  { value: "original", label: "Original" },
  { value: "44100", label: "44.1 kHz" },
  { value: "48000", label: "48 kHz" },
  { value: "96000", label: "96 kHz" },
];

export const CHANNEL_OPTIONS: Array<{ value: AudioChannels; label: string }> = [
  { value: "original", label: "Original" },
  { value: "1", label: "Mono" },
  { value: "2", label: "Stereo" },
];

export const AUDIO_FILE_ICON = FiMusic;
